<template>
<wind-profile-chart
  @wind-profile-chart-ready="onWindProfileChartReady"
  @wind-profile-chart-destroy="onWindProfileChartDestroy"
/>
</template>

<script>
import WindProfileChart from '../Charts/WindProfileChart'
import {WIND_PROFILE_CHART_DESTROY, WIND_PROFILE_CHART_READY} from '../Charts/WindProfileChart/EventKeys'
import {
  WIND_PROFILE_DATA_APPEND,
  WIND_PROFILE_DATA_INSERT,
  WIND_PROFILE_DATA_LIST_CHANGED,
  WIND_PROFILE_DATA_SELECT
} from './EventKeys'
import completeData from './completeData'

export default {
  name: 'WindProfileChartWrapper',
  components: {WindProfileChart},
  methods: {
    onWindProfileChartReady (windProfileChart) {
      this.windProfileChart = windProfileChart
      this.$emit(WIND_PROFILE_CHART_READY, windProfileChart)
      windProfileChart.$on(WIND_PROFILE_DATA_LIST_CHANGED, this[WIND_PROFILE_DATA_LIST_CHANGED])
      windProfileChart.$on(WIND_PROFILE_DATA_SELECT, this[WIND_PROFILE_DATA_SELECT])
      windProfileChart.$on(WIND_PROFILE_DATA_INSERT, this[WIND_PROFILE_DATA_INSERT])
      windProfileChart.$on(WIND_PROFILE_DATA_APPEND, this[WIND_PROFILE_DATA_APPEND])
    },
    onWindProfileChartDestroy () {
      let {windProfileChart} = this

      windProfileChart.$off(WIND_PROFILE_DATA_LIST_CHANGED, this[WIND_PROFILE_DATA_LIST_CHANGED])
      windProfileChart.$off(WIND_PROFILE_DATA_SELECT, this[WIND_PROFILE_DATA_SELECT])
      windProfileChart.$off(WIND_PROFILE_DATA_INSERT, this[WIND_PROFILE_DATA_INSERT])
      windProfileChart.$off(WIND_PROFILE_DATA_APPEND, this[WIND_PROFILE_DATA_APPEND])
      this.$emit(WIND_PROFILE_CHART_DESTROY, windProfileChart)

      this.windProfileChart = null
    },
    [WIND_PROFILE_DATA_LIST_CHANGED] (dataOpts) {
      dataOpts = this.dataOpts = {...dataOpts}
      this.completeValues(dataOpts)
      this.reverseHeights(dataOpts)
      this.reverseValues(dataOpts)
      this.setChartOpts(dataOpts)
    },
    [WIND_PROFILE_DATA_SELECT] (dataTime) {
      let {dataOpts} = this

      if (!dataOpts) {
        return
      }

      let {heightStart, heightEnd} = this
      let xIndex = dataOpts.values.findIndex(value => value.dataTime === dataTime)
      let yIndex = Math.round((heightEnd + heightStart) / 2) - heightStart

      this.windProfileChart.selectData(xIndex, yIndex)
    },
    [WIND_PROFILE_DATA_INSERT] (dataOpts) {
      let currentDataOpts = this.dataOpts
      dataOpts = {...dataOpts}

      currentDataOpts.beginTime = dataOpts.beginTime
      dataOpts.dataFrequency = currentDataOpts.dataFrequency

      this.completeValues(dataOpts)
      this.reverseValues(dataOpts)

      let {values} = dataOpts
      currentDataOpts.values = values.concat(currentDataOpts.values)

      let {heightStart, heightEnd} = this
      let chartOpts = {
        xAxis: {
          data: values.map(({dataTime}) => new Date(dataTime))
        },
        series: {
          data: this.getShowValues(values, heightStart, heightEnd)
        }
      }

      this.windProfileChart.insertData(chartOpts)
    },
    [WIND_PROFILE_DATA_APPEND] (dataOpts) {
      let currentDataOpts = this.dataOpts
      dataOpts = {...dataOpts}

      currentDataOpts.endTime = dataOpts.endTime
      dataOpts.dataFrequency = currentDataOpts.dataFrequency

      this.completeValues(dataOpts)
      this.reverseValues(dataOpts)

      let {values} = dataOpts
      currentDataOpts.values = currentDataOpts.values.concat(values)

      let {heightStart, heightEnd} = this
      let chartOpts = {
        xAxis: {
          data: values.map(({dataTime}) => new Date(dataTime))
        },
        series: {
          data: this.getShowValues(values, heightStart, heightEnd)
        }
      }

      this.windProfileChart.appendData(chartOpts)
    },
    completeValues (dataOpts) {
      dataOpts.values = completeData(dataOpts.beginTime, dataOpts.endTime, dataOpts.dataFrequency, dataOpts.values, dataTimeStr => ({
        windAngleId: null,
        windSpeedId: null,
        windAngles: null,
        windSpeeds: null,
        dataTime: dataTimeStr
      }), 0)
    },
    reverseHeights (dataOpts) {
      dataOpts.heights = [...dataOpts.heights].reverse()
    },
    reverseValues (dataOpts) {
      dataOpts.values = dataOpts.values.map(value => {
        let {windSpeeds, windAngles} = value

        if (Array.isArray(windSpeeds)) {
          windSpeeds = [...windSpeeds].reverse()
        }

        if (Array.isArray(windAngles)) {
          windAngles = [...windAngles].reverse()
        }

        return {
          ...value,
          windSpeeds,
          windAngles
        }
      })
    },
    setChartOpts (dataOpts) {
      let {showHeights, showValues} = this.getDataRange(dataOpts)
      let chartOpts = {
        tooltip: {
          xAxisFormatter: value => value ? value.format('YYYY-MM-DD HH:mm') : '',
          yAxisFormatter: value => value,
          speedFormatter: value => value,
          angleFormatter: value => value
        },
        xAxis: {
          fit: true,
          interval: 60 / dataOpts.dataFrequency,
          data: dataOpts.values.map(({dataTime}) => new Date(dataTime)),
          formatter: dataTime => dataTime.format('DD日HH时')
        },
        yAxis: {
          name: '高度',
          unit: '米',
          fit: true,
          interval: 2 + (showHeights.length & 1),
          data: showHeights,
          formatter: height => height
        },
        series: {
          name: '风廓线雷达',
          data: showValues,
          formatter: value => value
        }
      }

      this.windProfileChart.setOption(chartOpts, true)
    },
    getDataRange ({heights, values, minHeight, maxHeight}) {
      let [heightStart, heightEnd] = this.getHeightRange(minHeight, maxHeight, heights)
      let showHeights = heights.slice(heightStart, heightEnd)
      this.heightStart = heightStart
      this.heightEnd = heightEnd

      return {
        heightStart,
        heightEnd,
        showHeights,
        showValues: this.getShowValues(values, heightStart, heightEnd)
      }
    },
    getShowValues (values, heightStart, heightEnd) {
      return values.map(({windSpeeds, windAngles}) => {
        let list = []

        windSpeeds = windSpeeds || []
        windAngles = windAngles || []

        for (let i = heightStart; i < heightEnd; i++) {
          list.push([windSpeeds[i], windAngles[i]])
        }

        return list
      })
    },
    getHeightRange (minHeight, maxHeight, heights) {
      let end = heights.findIndex(height => height < minHeight)
      return [
        heights.findIndex(height => height <= maxHeight),
        end === -1 ? heights.length : end
      ]
    }
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/Data/ChartWrapper/WindProfileChartWrapper.vue